import { useEffect } from 'react';
import { Box, Group, Loader, Stack, Text } from '@mantine/core';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { useParams } from 'react-router-dom';
import type { ReactElement } from 'react';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { SurfaceEvidenceViewModel } from './SurfaceEvidenceViewModel';
import { findTopicInSurface, type AnalyticsSurface } from './AnalyticsSurface';
import { useSurface } from './useSurface';
import { fieldCarriesConfidence, formatCompact, formatCount, formatPercent, formatUsd, humanUser } from '@/shared/analytics/MetricFormatting';
import type { TopicDefinition } from './TopicCatalogTypes';
import type { TopicSlice } from './TopicSliceKey';
import SurfaceBreadcrumbRow from './SurfaceBreadcrumbRow';
import PageLevelFilterRow from './PageLevelFilterRow';
import StatBand, { type StatItem } from '@/design/StatBand';
import TopicSummaryPanel from './TopicSummaryPanel';
import SideRailPageLayout from './SideRailPageLayout';
import EvidenceTable from './EvidenceTable';
import SeededEvidenceTable from './SeededEvidenceTable';
import ProvenancePanel from './ProvenancePanel';
import EvidenceBreakdownPanel from './EvidenceBreakdownPanel';
import EvidenceTokenBreakdownPanel from './EvidenceTokenBreakdownPanel';
import SurfaceDrillBreakdownBody from './SurfaceDrillBreakdownBody';
import SessionDriftUserDetailBody from './SessionDriftUserDetailBody';
import FrustrationPromptsBody from './FrustrationPromptsBody';
import TopicNotFound from './TopicNotFound';
import SurfaceAssistScope, { SurfaceAssistToolbar } from './assist/SurfaceAssistScope';
import { useAssistFilters } from './assist/AssistSurfaceProvider';

const EVIDENCE_PAGE_SIZE = 25;

function sliceLabel(definition: TopicDefinition, slice: TopicSlice): string {
    const primary = definition.primaryField === 'user_id' ? humanUser(slice.primary) : slice.primary;
    if (slice.secondary === undefined) return primary;
    const secondaryField = definition.secondaryField ?? definition.drillField;
    const secondary = secondaryField === 'user_id' ? humanUser(slice.secondary) : slice.secondary;
    return `${primary} · ${secondary}`;
}

/** Overrides the topic breadcrumb crumb (label + link) — e.g. when the evidence is hosted under a different section's rail. */
export interface EvidenceTopicCrumb {
    label: string;
    to: string;
}

interface SurfaceEvidencePageProps {
    surfaceId: string;
    topicCrumb?: EvidenceTopicCrumb;
}

export default function SurfaceEvidencePage({ surfaceId, topicCrumb }: SurfaceEvidencePageProps): ReactElement {
    const surface = useSurface(surfaceId);
    if (surface === undefined) return <TopicNotFound />;
    return (
        <SurfaceAssistScope surface={surface}>
            <SurfaceEvidenceBody surface={surface} topicCrumb={topicCrumb} />
        </SurfaceAssistScope>
    );
}

/**
 * Routes the `:topicId/:primaryKey/:secondaryKey?` slice to the right body:
 * topics with a `drillField` get an intermediate breakdown (e.g. the people
 * behind a use case) at the bare primary key, and the record-level evidence
 * once the drill value is present.
 */
function SurfaceEvidenceBody({ surface, topicCrumb }: { surface: AnalyticsSurface; topicCrumb?: EvidenceTopicCrumb }): ReactElement {
    const { topicId, primaryKey, secondaryKey } = useParams<{ topicId: string; primaryKey: string; secondaryKey: string }>();
    const catalogDefinition = findTopicInSurface(surface, topicId);
    if (catalogDefinition?.anomalyKind === 'session_drift') {
        return <SessionDriftUserDetailBody surface={surface} />;
    }
    if (catalogDefinition?.anomalyKind === 're_prompt' && primaryKey !== undefined && primaryKey.length > 0) {
        return <FrustrationPromptsBody surface={surface} definition={catalogDefinition} primaryKey={primaryKey} />;
    }
    if (catalogDefinition?.drillField !== undefined && primaryKey !== undefined && primaryKey.length > 0 && (secondaryKey === undefined || secondaryKey.length === 0)) {
        return <SurfaceDrillBreakdownBody surface={surface} definition={catalogDefinition} primaryKey={primaryKey} />;
    }
    return <SurfaceEvidenceRecordsBody surface={surface} topicCrumb={topicCrumb} />;
}

function SurfaceEvidenceRecordsBody({ surface, topicCrumb }: { surface: AnalyticsSurface; topicCrumb?: EvidenceTopicCrumb }): ReactElement {
    const { topicId, primaryKey, secondaryKey } = useParams<{ topicId: string; primaryKey: string; secondaryKey: string }>();
    const vm = useViewModel(SurfaceEvidenceViewModel);
    const filters = useAssistFilters();

    useEffect(() => {
        void vm.load(surface.id, topicId, primaryKey, secondaryKey, filters);
    }, [vm, surface.id, topicId, primaryKey, secondaryKey, filters]);

    if (vm.notFound) return <TopicNotFound />;

    const definition = vm.definition;
    const slice = vm.slice;
    if (definition === null || slice === null) {
        return (
            <Group justify="center" gap="sm" py="xl">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                    Gathering evidence…
                </Text>
            </Group>
        );
    }

    const label = sliceLabel(definition, slice);
    const firstRecord = vm.records[0];
    const displayLabel = definition.primaryField === 'session_id' && firstRecord !== undefined ? `${humanUser(firstRecord.userId)}’s session` : label;
    const secondaryField = definition.secondaryField ?? definition.drillField;
    const sliceCrumbs =
        slice.secondary === undefined ?
            [{ label: displayLabel }]
        :   [
                {
                    label: definition.primaryField === 'user_id' ? humanUser(slice.primary) : slice.primary,
                    to: `${surface.basePath}/${definition.id}/${encodeURIComponent(slice.primary)}`,
                },
                { label: secondaryField === 'user_id' ? humanUser(slice.secondary) : slice.secondary },
            ];
    const summary = vm.summary;

    const consumption = surface.evidenceStyle === 'consumption';
    const stats: StatItem[] =
        consumption ?
            [
                { label: 'Prompts', value: formatCount(summary.turns) },
                { label: 'Total tokens', value: formatCompact(summary.totalTokens) },
                { label: 'Output tokens', value: formatCompact(summary.outputTokens) },
                { label: 'Cost', value: formatUsd(summary.allocatedCostUsd) },
            ]
        :   [
                { label: 'Prompts', value: formatCount(summary.turns) },
                { label: 'Sessions', value: formatCount(summary.sessions) },
                { label: 'Users', value: formatCount(summary.users) },
                { label: 'Output tokens', value: formatCompact(summary.outputTokens) },
            ];
    const statBandStats: StatItem[] =
        !consumption && fieldCarriesConfidence(definition.primaryField) ?
            [...stats, { label: 'Avg confidence', value: formatPercent(summary.avgConfidence) }]
        :   stats;

    return (
        <Box pb="xl">
            <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

            <SurfaceBreadcrumbRow
                items={[
                    ...(surface.drillStyle === 'side-rail' ? [] : [{ label: surface.title, to: surface.basePath }]),
                    topicCrumb ?? { label: definition.navLabel ?? definition.title, to: `${surface.basePath}/${definition.id}` },
                    ...sliceCrumbs,
                ]}
            />

            <PageHeaderComponent
                icon={definition.icon}
                eyebrow={`Evidence · ${definition.lensLabel}`}
                title={displayLabel}
                subtitle={`The ${formatCount(vm.totalMatched)} prompt${vm.totalMatched === 1 ? '' : 's'} behind this slice — every one classified, with its confidence and source.`}
            />

            {surface.drillStyle !== 'side-rail' && (
                <Box mb="lg">
                    <StatBand stats={statBandStats} />
                </Box>
            )}

            {vm.errorMessage !== null && (
                <Text size="sm" c="red" mb="md">
                    {vm.errorMessage}
                </Text>
            )}

            {vm.loading && vm.records.length === 0 ?
                <Group justify="center" gap="sm" py="xl">
                    <Loader size="sm" />
                </Group>
            :   <SideRailPageLayout
                    rail={
                        <Stack gap="md">
                            {(surface.drillStyle === 'side-rail' || consumption) && <TopicSummaryPanel stats={stats} />}
                            {consumption ?
                                <EvidenceTokenBreakdownPanel records={vm.records} />
                            :   <>
                                    <ProvenancePanel definition={definition} records={vm.records} confidenceOnly />
                                    <EvidenceBreakdownPanel records={vm.records} />
                                </>
                            }
                        </Stack>
                    }
                >
                    {consumption && surface.evidenceColumns !== undefined ?
                        <SeededEvidenceTable
                            records={vm.records}
                            accent={definition.accent}
                            columns={surface.evidenceColumns}
                            persistenceKey={`insight-evidence:${surface.id}:${definition.id}`}
                            clientPageSize={EVIDENCE_PAGE_SIZE}
                            defaultSortKey="total"
                        />
                    :   <EvidenceTable
                            records={vm.records}
                            accent={definition.accent}
                            persistenceKey={`insight-evidence:${surface.id}:${definition.id}`}
                            columns={surface.evidenceColumns}
                            clientPageSize={EVIDENCE_PAGE_SIZE}
                        />
                    }
                </SideRailPageLayout>
            }
        </Box>
    );
}
